import React from 'react'
import { Modal, Form, Col, Row, Button } from 'react-bootstrap'
import { createModal, ModalResolveType, useModalAction } from '@src/lib/modal-manager'
import { useFetch } from '@src/hooks'
import { useForm, Controller } from '@src/lib/react-hook-form'
import z from 'zod'
import { ShiftDto } from '@src/domain/repositories/setting.repo'
import { UserService } from '@src/domain/services'

interface EditShiftProps {
  dtoUuid?: string;
  itemDto?: ShiftDto;
}

const initState: ShiftDto = {
  code: '',
  description: '',
  factoryUuid: '',
  status: '1'
}

export function EditShiftModalWrapper(props: EditShiftProps) {
  const { resolve, remove, hide } = useModalAction()
  const { dtoUuid, itemDto } = props

  const { register, handleSubmit, formState: { errors }, control } = useForm<ShiftDto>({
    reValidateMode: 'onSubmit',
    defaultValues: {...initState, ...itemDto},
    rules: z.object({
      code: z.string().nonempty({ message: '請輸入班別代碼' }),
      description: z.string().nonempty({ message: '請輸入班別描述' }),
      factoryUuid: z.string().nonempty({ message: '請選擇廠區' }),
      status: z.string().min(1, {message: '請選擇狀態'})
    })
  })
  
  const { data: factoryOptions = [] } = useFetch(UserService.getFactories, null)

  const handleClose = () => {
    resolve(ModalResolveType.CLOSED)
    hide()
    remove()
  }

  const handleCancel = () => {
    resolve(ModalResolveType.CANCEL)
    hide()
    remove()
  }

  const handleOnSubmit = (formDto: ShiftDto) => {
    resolve(ModalResolveType.OK, formDto)
    hide()
    remove()
  }

  return (
    <Modal size='lg' show onHide={handleClose} centered>
      <Form onSubmit={handleSubmit(handleOnSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>{`${dtoUuid ? '編輯' : '新增'}班別`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group as={Row} className='mb-3'>
            <Form.Label column sm='2'>
              廠區
            </Form.Label>
            <Col sm='10'>
              <Controller
                name='factoryUuid'
                control={control}
                render={({ field }) => <Form.Select {...field}
              >
                  <option value=''>請選擇廠區</option>
                  {
                    factoryOptions
                      .filter(o => o.code !== 'ALL')
                      .map(option => <option key={option.uuid} value={option.uuid}>{`${option.code}-${option.description}`}</option>)
                  }
                </Form.Select>}
              />
            </Col>
            {errors.factoryUuid && <Form.Text className='text-danger'>{errors.factoryUuid.message}</Form.Text>}
          </Form.Group>

          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm="2">
              班別代碼
            </Form.Label>
            <Col sm="10">
              <Form.Control placeholder="Ex: S01" {...register('code')} />
            </Col>
            {errors.code && <Form.Text className='text-danger'>{errors.code.message}</Form.Text>}
          </Form.Group>

          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm="2">
              班別描述
            </Form.Label>
            <Col sm="10">
              <Form.Control placeholder="早班" {...register('description')} />
            </Col>
            {errors.description && <Form.Text className='text-danger'>{errors.description.message}</Form.Text>}
          </Form.Group>
          <Form.Group as={Row} className='mb-3'>
            <Form.Label column sm='2'>
              狀態
            </Form.Label>
            <Col sm='10'>
              <Form.Select {...register('status')}>
                <option value='1'>啟用</option>
                <option value='0'>停用</option>
              </Form.Select>
            </Col>
            {errors.status && <Form.Text className='text-danger'>{errors.status.message}</Form.Text>}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleCancel} variant='light'>
            取消
          </Button>
          <Button type='submit'>
            確認
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export const EditShiftModal = createModal(EditShiftModalWrapper)
